import React, { useState, useEffect } from "react";
import { Instagram, Sparkles, Star, X, CheckCircle2, Send } from "lucide-react";
import api from "../api";
import { T, primaryBtn, inputStyle, todayLabel, weekDays } from "../theme";
import { InfoHint, VikiBadge } from "./shared";
import { VikiCaptionButton, VikiMultiFormatButton } from "./VikiWidgets";

export default function ContentTab({ profile, staticData, actions, generatingAllCaptions }) {
  const [selectedPillar, setSelectedPillar] = useState(Object.keys(staticData.pillars)[0]);
  const [bestDays, setBestDays] = useState(null);
  const publishedCount = Object.values(profile.calendar || {}).flat().filter((e) => e.status === "programmato").length;
  const historyCount = profile.history.length;

  useEffect(() => {
    api.get(`/profiles/${profile.id}/best-days`).then(({ data }) => setBestDays(data)).catch(() => {});
  }, [profile.id, publishedCount, historyCount]);

  const starDays = bestDays?.best_days || staticData.bestPostingDays;

  return (
    <div data-testid="content-tab">
      <div style={{
        display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center", gap: 12,
        background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: "12px 16px", marginBottom: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Instagram size={16} color={profile.instagram_connected ? T.teal : T.textDim} />
          <div data-testid="instagram-status-text" style={{ fontSize: 13, color: T.textDim }}>
            {profile.instagram_connected
              ? <>Instagram collegato (con la Pagina Facebook a esso collegata): puoi programmare la pubblicazione direttamente dal calendario.</>
              : <>Collega Instagram e la Pagina Facebook collegata per programmare la pubblicazione dei contenuti da qui.</>}
          </div>
        </div>
        <button data-testid="instagram-toggle-btn" onClick={actions.toggleInstagram} style={{
          ...primaryBtn, background: profile.instagram_connected ? "transparent" : T.amber,
          color: profile.instagram_connected ? T.textDim : T.ink,
          border: profile.instagram_connected ? `1px solid ${T.line}` : "none",
          flexShrink: 0,
        }}>{profile.instagram_connected ? "Scollega" : "Collega Instagram"}</button>
      </div>
      <div style={{ fontSize: 11, color: T.textDim, marginBottom: 18 }}>TikTok e altre piattaforme: nella roadmap, non ancora disponibili. Il collegamento a Instagram in questa versione è simulato.</div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 12, color: T.textDim, maxWidth: 420 }}>Non sai da dove iniziare? Lascia che sia V.I.K.I. a mettere le prime idee in calendario, e a scrivere le didascalie per te.</div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          <button data-testid="content-generate-all-captions-btn" onClick={actions.generateAllCaptions} disabled={generatingAllCaptions} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.viki}`, color: T.viki, borderRadius: 7, padding: "8px 14px", fontSize: 12, cursor: generatingAllCaptions ? "default" : "pointer", opacity: generatingAllCaptions ? 0.6 : 1 }}>
            <Sparkles size={13} /> {generatingAllCaptions ? "Sto scrivendo…" : "Scrivi tutte le didascalie"}
          </button>
          <button data-testid="content-autofill-week-btn" onClick={actions.autoFillWeek} style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${T.viki}`, color: T.viki, borderRadius: 7, padding: "8px 14px", fontSize: 12, cursor: "pointer" }}>
            <Sparkles size={13} /> Compila la settimana per me
          </button>
        </div>
      </div>

      <div className="stack-on-mobile" style={{ gap: 24 }}>
        <div style={{ width: 320, flexShrink: 0 }}>
          <div style={{ fontSize: 13, color: T.textDim, marginBottom: 8 }}>Tipo di contenuto</div>
          <select data-testid="content-pillar-select" value={selectedPillar} onChange={(e) => setSelectedPillar(e.target.value)} style={{ ...inputStyle, marginBottom: 14 }}>
            {Object.keys(staticData.pillars).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {staticData.pillars[selectedPillar].map((idea, idx) => (
              <div key={idx} data-testid={`content-idea-${idx}`} style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 8, padding: 12 }}>
                <div style={{ fontSize: 13, marginBottom: 8, lineHeight: 1.4 }}>{idea}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                  {weekDays.map((d) => (
                    <button data-testid={`content-idea-${idx}-day-${d}`} key={d} onClick={() => actions.addToCalendar(d, idea)} style={{ fontSize: 11, padding: "3px 7px", borderRadius: 4, background: "transparent", border: `1px solid ${T.line}`, color: T.textDim, cursor: "pointer" }}>{d}</button>
                  ))}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <VikiCaptionButton ideaText={idea} profileId={profile.id} />
                  <VikiMultiFormatButton ideaText={idea} profileId={profile.id} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, flexWrap: "wrap" }}>
            <div style={{ fontSize: 13, color: T.textDim }}>Calendario settimanale</div>
            <div style={{ fontSize: 11, color: T.textDim }}>Tocca il pallino per segnare come approvato{profile.instagram_connected ? ' · poi "Pubblica ora" quando sei pronto' : ""}</div>
          </div>
          <div data-testid="content-best-days-tip" style={{ fontSize: 11, color: T.textDim, marginBottom: 10, display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
            {bestDays?.mode === "personale" ? (
              <>
                <VikiBadge /> <Star size={11} color={T.viki} fill={T.viki} /> <b style={{ color: T.viki }}>{starDays.join(" e ")}</b> sono i tuoi giorni migliori, calcolati sui tuoi risultati reali<InfoHint text={bestDays.message} />
              </>
            ) : (
              <>
                <Star size={11} color={T.amber} /> Mer e Gio sono i giorni con più interazione secondo i dati più recenti<InfoHint text={`${staticData.bestPostingTip}${bestDays ? ` — ${bestDays.message}` : ""}`} />
              </>
            )}
          </div>
          <div className="grid-week" data-testid="content-week-calendar">
            {weekDays.map((d) => (
              <div key={d} data-testid={`calendar-day-${d}`} style={{ background: T.surface, border: `1px solid ${d === todayLabel() ? T.amber : T.line}`, borderRadius: 8, padding: 10, minHeight: 160, alignSelf: "start" }}>
                <div style={{ fontFamily: T.fontHead, fontSize: 12, marginBottom: 8, color: d === todayLabel() ? T.amber : T.textDim, display: "flex", alignItems: "center", gap: 4 }}>
                  {d}{starDays.includes(d) && <Star size={10} color={bestDays?.mode === "personale" ? T.viki : T.amber} fill={bestDays?.mode === "personale" ? T.viki : T.amber} />}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {(profile.calendar[d] || []).map((entry) => {
                    const dotColor = entry.status === "approvato" ? T.amber : T.textDim;
                    const statusLabel = entry.status === "approvato" ? "Approvato" : "Bozza";
                    return (
                      <div key={entry.id} data-testid={`calendar-entry-${entry.id}`} style={{ fontSize: 11, background: T.surfaceAlt, borderRadius: 5, padding: 6, color: T.text, position: "relative", lineHeight: 1.3 }}>
                        <button data-testid={`calendar-entry-remove-${entry.id}`} onClick={() => actions.removeFromCalendar(entry.id)} style={{ position: "absolute", top: 2, right: 2, background: "transparent", border: "none", color: T.textDim, cursor: "pointer" }}><X size={11} /></button>
                        <div style={{ paddingRight: 14, marginBottom: 4 }}>{entry.text}</div>
                        <div onClick={(e) => e.stopPropagation()}>
                          <VikiCaptionButton
                            ideaText={entry.text} profileId={profile.id}
                            savedCaption={entry.caption} onGenerated={(cap) => actions.saveCaption(entry.id, cap)}
                          />
                        </div>
                        {entry.status === "programmato" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 4, color: T.teal, fontSize: 10, fontWeight: 600, marginTop: 4 }}>
                            <CheckCircle2 size={11} /> Pubblicato
                          </div>
                        ) : (
                          <div style={{ marginTop: 4 }}>
                            <button data-testid={`calendar-entry-status-${entry.id}`} onClick={() => actions.setEntryStatus(entry.id, entry.status === "bozza" ? "approvato" : "bozza")} title="Tocca per segnare come approvato o tornare a bozza" style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: "none", color: T.textDim, cursor: "pointer", padding: 0, fontSize: 10, marginBottom: entry.status === "approvato" && profile.instagram_connected ? 6 : 0 }}>
                              <span style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, display: "inline-block" }} />
                              {statusLabel}
                            </button>
                            {entry.status === "approvato" && profile.instagram_connected && (
                              <button data-testid={`calendar-entry-publish-${entry.id}`} onClick={() => actions.setEntryStatus(entry.id, "programmato")} style={{ display: "flex", alignItems: "center", gap: 4, background: T.amber, color: T.ink, border: "none", borderRadius: 5, padding: "4px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", width: "100%", justifyContent: "center" }}>
                                <Send size={10} /> Pubblica ora
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
